import { injectable } from 'inversify';

@injectable()
export abstract class ConvergingTask<TResult, TInput = null> {
	private sequenceNumber = 0;
	private ongoing = false;
	private committing = false;
	private canceledUpTo = -1;
	private containerOfInputAsReceived: readonly TInput[] = [];

	private get notOngoing(): boolean {
		return !this.ongoing;
	}

	private get notCommitting(): boolean {
		return !this.committing;
	}

	public cancel(): void {
		this.canceledUpTo = this.sequenceNumber;
	}

	private isCanceled(sequenceNumber: number): boolean {
		return sequenceNumber <= this.canceledUpTo;
	}

	protected async execute(input: TInput): Promise<void> {
		if (this.cannotExecute()) {
			return;
		}

		const sequenceNumber = ++this.sequenceNumber;
		this.containerOfInputAsReceived = [input];

		if ((this.queued() && this.notOngoing) || (!this.queued() && this.notCommitting)) {
			await this.doExecute(sequenceNumber);
			this.afterExecution();
		}

		this.afterAttempt();
	}

	private async doExecute(sequenceNumber: number): Promise<void> {
		if (this.containerOfInputAsReceived.length === 0) {
			return;
		}

		if (this.isCanceled(sequenceNumber)) {
			return;
		}

		const input = this.containerOfInputAsReceived[0];
		this.ongoing = true;
		try {
			const runPromise = this.run(input);
			this.beforeEachRun(runPromise, input);

			const result = await runPromise;
			if (sequenceNumber === this.sequenceNumber && !this.isCanceled(sequenceNumber)) {
				this.committing = true;
				try {
					await this.commit(result);
				} finally {
					this.committing = false;
				}
			}
		} catch (error) {
			this.handleError(error);
			throw error;
		} finally {
			this.ongoing = false;

			this.afterEachRun();
		}

		if (sequenceNumber !== this.sequenceNumber && this.queued()) {
			await this.doExecute(this.sequenceNumber);
		}
	}

	protected abstract run(input: TInput): Promise<TResult>;

	protected abstract commit(result: TResult): void | Promise<void>;

	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	protected handleError(_error: unknown): void {
		// Overridden by specializations
	}

	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	protected beforeEachRun(_runPromise: Promise<unknown>, input: TInput): void {
		// Overridden by specializations
	}

	protected afterEachRun(): void {
		// Overridden by specializations
	}

	protected afterExecution(): void {
		// Overridden by specializations
	}

	protected afterAttempt(): void {
		// Overridden by specializations
	}

	protected canExecute(): boolean {
		return true;
	}

	private cannotExecute(): boolean {
		return !this.canExecute();
	}

	protected queued(): boolean {
		return true;
	}
}
